import { useState, useEffect, useCallback, useRef } from 'react';
import api, { getErrorMessage } from '../services/api';
import Layout from '../components/Layout';

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ── Toast ────────────────────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 5000); return () => clearTimeout(t); }, [onClose]);
  const styles = { success: 'bg-emerald-50 border-emerald-200 text-emerald-800', error: 'bg-red-50 border-red-200 text-red-800' };
  const icons  = { success: 'M5 13l4 4L19 7', error: 'M6 18L18 6M6 6l12 12' };
  return (
    <div className={`fixed top-20 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-lg animate-slide-down ${styles[type]}`}>
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icons[type]} />
      </svg>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// ── Add/Edit Material Modal ──────────────────────────────────────────────────

const MaterialModal = ({ batches, classes, onSave, onCancel, loading, initial }) => {
  const [form, setForm] = useState({
    title:       initial?.title       || '',
    description: initial?.description || '',
    class_id:    initial?.class_id    || '',
    batch_id:    initial?.batch_id    || '',
    folder_link: initial?.folder_link || '',
  });
  const [formError, setFormError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormError('');
  };

  const filteredClasses = form.batch_id
    ? classes.filter((c) => c.batch_id === form.batch_id)
    : classes;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim())       return setFormError('Title is required.');
    if (!form.folder_link.trim()) return setFormError('Cloud folder link is required.');
    if (!form.batch_id && !form.class_id) return setFormError('Please select a batch or class.');

    onSave({
      title:       form.title.trim(),
      description: form.description.trim() || null,
      class_id:    form.class_id  || null,
      batch_id:    form.batch_id  || null,
      folder_link: form.folder_link.trim(),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="text-lg font-bold text-gray-900">{initial ? 'Edit Material' : 'Add Material'}</h3>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{formError}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
            <input ref={inputRef} type="text" name="title" value={form.title} onChange={handleChange} required
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              placeholder="e.g. Week 3 – React Hooks Resources" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea name="description" value={form.description} onChange={handleChange} rows={2}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm resize-none"
              placeholder="Brief description of the folder contents..." />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Cloud Folder Link</label>
            <input type="url" name="folder_link" value={form.folder_link} onChange={handleChange} required
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              placeholder="https://drive.google.com/drive/folders/..." />
            <p className="text-xs text-gray-400 mt-1">Google Drive, OneDrive, Dropbox, or any shareable folder URL.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Batch <span className="text-gray-400 font-normal">(optional)</span></label>
              <select name="batch_id" value={form.batch_id} onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white">
                <option value="">No batch</option>
                {batches.map((b) => <option key={b.id} value={b.id}>{b.batch_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Class <span className="text-gray-400 font-normal">(optional)</span></label>
              <select name="class_id" value={form.class_id} onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white">
                <option value="">No class</option>
                {filteredClasses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onCancel} disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">Cancel</button>
            <button type="submit" disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50">
              {loading ? 'Saving...' : initial ? 'Save Changes' : 'Add Material'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────

const TrainerMaterials = () => {
  const [materials, setMaterials] = useState([]);
  const [batches, setBatches] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => setToast({ message, type });

  const fetchMaterials = useCallback(async () => {
    try {
      const res = await api.get('/api/materials');
      const raw = res.data;
      setMaterials(Array.isArray(raw) ? raw : raw?.materials || raw?.data || []);
    } catch { setError('Failed to load materials.'); }
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [batchRes, classRes] = await Promise.all([api.get('/api/batches'), api.get('/api/classes')]);
      const rb = batchRes.data;
      setBatches(Array.isArray(rb) ? rb : rb?.batches || rb?.data || []);
      const rc = classRes.data;
      setClasses(Array.isArray(rc) ? rc : rc?.classes || rc?.data || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMaterials(); fetchAll(); }, []); // eslint-disable-line

  const handleAdd = async (form) => {
    setSaveLoading(true);
    try {
      await api.post('/api/materials', form);
      setShowAdd(false);
      showToast('Material added successfully');
      await fetchMaterials();
    } catch (err) { showToast(getErrorMessage(err, 'Failed to add material'), 'error'); }
    finally { setSaveLoading(false); }
  };

  const handleEdit = async (form) => {
    setSaveLoading(true);
    try {
      await api.put(`/api/materials/${editing.id}`, form);
      setEditing(null);
      showToast('Material updated');
      await fetchMaterials();
    } catch (err) { showToast(getErrorMessage(err, 'Failed to update material'), 'error'); }
    finally { setSaveLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this material?')) return;
    try {
      await api.delete(`/api/materials/${id}`);
      showToast('Material deleted');
      setMaterials((prev) => prev.filter((m) => m.id !== id));
    } catch (err) { showToast(getErrorMessage(err, 'Failed to delete material'), 'error'); }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="skeleton h-8 w-48" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="skeleton h-5 w-40 mb-2" /><div className="skeleton h-4 w-64" />
              </div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button onClick={() => window.location.reload()} className="px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium">Retry</button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Materials</h1>
              <p className="text-sm text-gray-500">{materials.length} folder links shared</p>
            </div>
          </div>
          <button onClick={() => setShowAdd(true)}
            className="px-4 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-colors flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Material
          </button>
        </div>

        {/* Material List */}
        {materials.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center justify-center py-14 text-gray-400">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-500">No materials added yet</p>
              <p className="text-xs text-gray-400 mt-1">Use the button above to share a cloud folder link</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(
              materials.reduce((groups, m) => {
                const key = m.batch_name || 'General';
                if (!groups[key]) groups[key] = [];
                groups[key].push(m);
                return groups;
              }, {})
            ).map(([batchName, items]) => (
              <div key={batchName}>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">{batchName}</h2>
                <div className="space-y-3">
                  {items.map((m) => (
                    <div key={m.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1 min-w-0 mr-4">
                          <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900">{m.title}</h3>
                            {m.description && <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{m.description}</p>}
                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                              {m.class_title && <span>{m.class_title}</span>}
                              <span>Added {formatDate(m.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <a href={m.folder_link} target="_blank" rel="noopener noreferrer"
                            className="px-3 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 transition-colors">
                            Open Folder
                          </a>
                          <button onClick={() => setEditing(m)}
                            className="px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-200 transition-colors">
                            Edit
                          </button>
                          <button onClick={() => handleDelete(m.id)}
                            className="px-3 py-2 bg-red-50 text-red-600 text-sm font-medium rounded-xl hover:bg-red-100 transition-colors">
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <MaterialModal batches={batches} classes={classes} onSave={handleAdd} onCancel={() => setShowAdd(false)} loading={saveLoading} />
      )}
      {editing && (
        <MaterialModal batches={batches} classes={classes} onSave={handleEdit} onCancel={() => setEditing(null)} loading={saveLoading} initial={editing} />
      )}
    </Layout>
  );
};

export default TrainerMaterials;
